/**
 * Admin accounts — `/v1/admin/admins*`. Superadmin-only, enforced by the server.
 *
 * The interesting rules here are all the server's, and this module deliberately does not reimplement
 * any of them: the **last-superadmin invariant** (you cannot remove the grant that would leave
 * nobody able to grant), the refusal to let a superadmin reset their own authenticator, and the fact
 * that changing someone's scopes revokes their sessions so a stale claim cannot outlive the change.
 * The screen surfaces those refusals; it does not pre-empt them, because a client that guesses at a
 * server invariant is a client that will one day be wrong and confident.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type AdminRow = components['schemas']['AdminRow'];
type AdminList = components['schemas']['AdminListResponse'];
export type ScopePreset = components['schemas']['ScopePreset'];
type ScopePresets = components['schemas']['ScopePresetsResponse'];

export interface AdminQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  q?: string;
  is_active?: boolean;
}

/** Exported for its own test: `is_active: false` is a filter, `undefined` is the absence of one. */
export function adminQueryString(params: AdminQuery): string {
  const search = new URLSearchParams({ page: String(params.page), page_size: String(params.page_size) });
  if (params.q) search.set('q', params.q);
  if (params.is_active !== undefined) search.set('is_active', String(params.is_active));
  return search.toString();
}

export function useAdmins(params: AdminQuery, enabled = true) {
  return useQuery({
    queryKey: keys.admins.list(params),
    queryFn: () => authedClient.request<AdminList>(`/v1/admin/admins?${adminQueryString(params)}`),
    enabled,
  });
}

/**
 * The invitation form's presets — the `admin.scope_presets` setting (doc 07 §8), so ops reshape
 * them in the settings console rather than in a deploy.
 */
export function useScopePresets(enabled = true) {
  return useQuery({
    queryKey: keys.admins.presets(),
    queryFn: () => authedClient.request<ScopePresets>('/v1/admin/admins/scope-presets'),
    enabled,
  });
}

/** `Ops (ops_dispatch + support)` — the name, then what it grants; a preset granting nothing is its name alone. */
export function presetLabel(preset: ScopePreset): string {
  return preset.scopes.length ? `${preset.name} (${preset.scopes.join(' + ')})` : preset.name;
}

/** Invalidates the whole `admins` namespace: any page of any filter may now be stale. */
function useAdminMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.admins.all() }),
  });
}

export interface InviteAdmin {
  email: string;
  full_name: string;
  scopes: string[];
}

export function useInviteAdmin() {
  return useAdminMutation((body: InviteAdmin) =>
    authedClient.request<AdminRow>('/v1/admin/admins', { method: 'POST', body }),
  );
}

export function useSetScopes() {
  return useAdminMutation(({ id, scopes, reason }: { id: number; scopes: string[]; reason: string }) =>
    authedClient.request<void>(`/v1/admin/admins/${id}/scopes`, { method: 'PUT', body: { scopes, reason } }),
  );
}

export function useSetActive() {
  return useAdminMutation(({ id, is_active, reason }: { id: number; is_active: boolean; reason: string }) =>
    authedClient.request<void>(`/v1/admin/admins/${id}`, { method: 'PATCH', body: { is_active, reason } }),
  );
}

export function useResetTotp() {
  return useAdminMutation(({ id, reason }: { id: number; reason: string }) =>
    authedClient.request<void>(`/v1/admin/admins/${id}/totp-reset`, { method: 'POST', body: { reason } }),
  );
}

export function useRevokeAdminSessions() {
  return useAdminMutation(({ id, reason }: { id: number; reason: string }) =>
    authedClient.request<void>(`/v1/admin/admins/${id}/sessions/revoke`, { method: 'POST', body: { reason } }),
  );
}

export function useDeleteAdmin() {
  return useAdminMutation(({ id, reason }: { id: number; reason: string }) =>
    authedClient.request<void>(`/v1/admin/admins/${id}`, { method: 'DELETE', body: { reason } }),
  );
}
